<script setup lang="ts">
import { computed, ref } from 'vue';
import { useThemeOverrides } from '@apparatix/composables';
import { useContactStore } from '@apparatix/stores/useContactStore';
import DataTable, { DataTableRowClickEvent } from 'primevue/datatable';
import Column from 'primevue/column';
import ToggleSwitch from 'primevue/toggleswitch';
import Badge from 'primevue/badge';
import { Section } from '@apparatix/library/core';
import type { WithNavigation } from './types';

type Emit = WithNavigation<{
  (e: 'update:visible', visible: boolean): void;
}>;

const emit = defineEmits<Emit>();

const store = useContactStore();
const { datatable } = useThemeOverrides();

const showAllCampaigns = ref(false);
const visibleCampaigns = computed(() => {
  if (showAllCampaigns.value) return store.contactAssociations.campaigns;
  return store.contactAssociations.campaigns.filter(campaign => campaign.isActive);
});

const onRowClick = (e: DataTableRowClickEvent) => {
  emit('navigate-forward', { view: e.data.type, id: e.data.id, tabId: 'deals' });
};

const rowClass = () => 'cursor-pointer';

</script>
<template>
  <div class="w-full flex flex-col gap-6">
    <Section class="mb-3" :footer="false">
      <DataTable
        :value="visibleCampaigns"
        table-style="min-width: 50rem;"
        class="no-left-padding"
        :dt="datatable.noLeftPadding"
        :row-class="rowClass"
        @row-click="onRowClick"
      >
        <template #header>
          <div class="flex items-center justify-between gap-4">
            <h3 class="text-base font-medium text-[var(--swimlane-primary-text)]">
              Leads &amp; Opportunities
              <Badge :value="visibleCampaigns.length" severity="secondary" />
            </h3>
            <div class="flex items-center gap-2">
              Show All
              <ToggleSwitch v-model="showAllCampaigns" />
            </div>
          </div>
        </template>
        <template #empty>
          <p v-show="showAllCampaigns">
            No leads or opportunities exist for this contact.
          </p>
          <p v-show="! showAllCampaigns">
            No active leads or opportunities exist for this contact.
          </p>
        </template>
        <template v-if="visibleCampaigns.length > 0">
          <Column
            field="name"
            header="Name"
            sortable
            class="text-[var(--text-link-primary)] cursor-pointer"
          />
          <Column field="status" header="Status" sortable />
          <Column field="budgetDescription" header="Budget Details" sortable />
          <Column
            field="createdDate"
            header="Created"
            sortable
            :sort-field="'age'"
          />
          <Column field="age" header="Age (Days)" sortable />
        </template>
      </DataTable>
    </Section>
  </div>
</template>
